import { Injectable } from "@angular/core";
// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

// order-entry services
import { OESettingsService } from "./basic/oe-settings.service";
import { OEModelGetterService } from "./model/oe-model-getter.service";
import { OECoordinatorService } from "./oe-coordinator.service";
import { OEActionsService } from "./oe-actions.service";
import { OENDisplayState } from "../data/enums/oe-display-state.enum";
import { Subject } from "rxjs";
import { OENActionCommand } from "../data/enums/oe-action-command.enum";
import { OEActionCommandModel } from "../data/models/oe-action-command.model";
import { OENCommand } from "app/order-entry/data/enums/oe-command.enum";

@Injectable()
export class OECloserService {
  // actions
  private oeActionsControlSource = new Subject<OEActionCommandModel>();
  public actionCtrl$ = this.oeActionsControlSource.asObservable();

  /**********************************************************************/
  /* CONSTRUCTOR  */

  constructor(
    private settings: OESettingsService,
    private getter: OEModelGetterService,
    private coordinator: OECoordinatorService, //private actions: OEActionsService
  ) {}

  /**********************************************************************/
  /* METHODS  */

  /**********************************************************************/
  /* ORDER-ENTRY FORM CLOSE  */

  // used in actions panel, the event triggers actionDestroyOnClose()
  public actionIssueCloseOrderEntryCmd(refId: string): void {
    if (this.getter.isOperationModeModify(refId) || this.getter.isOperationModeAmendMarkup(refId)) {
      // if modify or amend markup, dont autClose right away go through onClose() modify logic
      // a dialog may be needed
      this.coordinator.cmdCloseOrderEntryScreen(refId, false);
    } else {
      this.coordinator.cmdCloseOrderEntryScreen(refId);
    }
    //this.destroyAllOrderEntryStrategyInstances(refId);
  }

  /* IMPORTANT: Do not invoke order entry form close (e.g. this.coordinator.cmdCloseOrderEntryScreen(refId))
				  or call other methods that invokes form close. Why? because ∞ loop */
  public actionDestroyOnClose(refId: string): void {
    //this.actions.destroyInstances(refId);
    this.oeActionsControlSource.next(
      new OEActionCommandModel(OENActionCommand.DESTROY_INSTANCES, refId),
    );
  }

  /* IMPORTANT: Do not invoke order entry form close (e.g. this.coordinator.cmdCloseOrderEntryScreen(refId))
				or call other methods that invokes form close. Why? no ∞ loop, but just to be on the save side :) */
  //actionExtendedDestroyOnClose
  public performCloseAndDestroy(refId: string): void {
    if (this.getter.has(refId)) {
      if (this.getter.isOperationModeModify(refId) && this.getter.isLockedForModification(refId)) {
        let orderState = this.getter.getOrderStateForModifyIssuer(refId);
        switch (orderState) {
          case CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED:
            //this.actions.abort(refId);
            this.oeActionsControlSource.next(
              new OEActionCommandModel(OENActionCommand.ABORT, refId),
            );
            break;
          case CubiqTS.OrderState.IN_MODIFICATION:
          // NB : This is already catered for by the closeAndUnlockModify
          // and we don;t wnat the default - abort - behaviour so
          // NOP
          // 	this.removeModifyLocks(refId);
          case CubiqTS.OrderState.ACCEPTANCE_NEEDED:
          case CubiqTS.OrderState.DI_NEEDED: /// ADDED AS PART OF OMS-28059
          case CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED:
          case CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED:
          case CubiqTS.OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED:
          case CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED:
            // do nothing
            //	this.actionAbort(refId);
            break;
          default:
            let lockState = this.getter.isLockedForModification(refId);
            if (lockState) {
              //this.actions.abort(refId);
              this.oeActionsControlSource.next(
                new OEActionCommandModel(OENActionCommand.ABORT, refId),
              );
            } else {
              logger.out.error(
                `ERROR: _oeActions.actionExtendedDestroyOnClose(): OrderSate does not trigger action when locked! state: ${CubiqTS.OrderState[orderState]} `,
              );
            }
            break;
        }
      } else if (
        this.getter.isOperationModeNew(refId) ||
        this.getter.isOperationModeDuplicate(refId)
      ) {
        let displayState = this.getter.getDisplayState(refId);
        switch (displayState) {
          case OENDisplayState.SHOW_ORDER_ENTRY:
          case OENDisplayState.SHOW_SUCCESS:
          case OENDisplayState.SHOW_MONITOR_EXECUTION:
          case OENDisplayState.SHOW_REJECTED:
          /// STATES ADDED AS PART OF OMS-28059
          case OENDisplayState.SHOW_APPROVAL_ALLOWED:
          case OENDisplayState.SHOW_ACCEPTANCE_ALLOWED:
            break;
          default:
            //this.actions.abort(refId);
            this.oeActionsControlSource.next(
              new OEActionCommandModel(OENActionCommand.ABORT, refId),
            );
            break;
        }
      } else if (this.getter.isOperationModeAmendMarkup(refId)) {
        if (this.getter.getDisplayState(refId) === OENDisplayState.SHOW_AMEND_MARKUP) {
          this.removeAmendMarkupLocks(refId);
        }
      }
      //this.actions.destroyInstances(refId);
      this.oeActionsControlSource.next(
        new OEActionCommandModel(OENActionCommand.DESTROY_INSTANCES, refId),
      );
    }
  }

  negativeClose(refId) {
    let activeRefId = this.getter.getActivelyDisplayedRefId(refId);
    activeRefId = activeRefId != null ? activeRefId : refId;
    this.coordinator.cmdChangeDisplayState(activeRefId, OENCommand.STATE_REVAL_BUTTONS);
  }

  //internalClosePopout
  public close(refId: string): void {
    let activeRefId = this.getter.getActivelyDisplayedRefId(refId);
    activeRefId = activeRefId != null ? activeRefId : refId;
    this.coordinator.cmdCloseOrderEntryScreen(activeRefId);
  }

  //internalAbortAndClosePopout
  public closeAndAbort(refId: string): void {
    //this.actions.abort(refId);
    this.oeActionsControlSource.next(new OEActionCommandModel(OENActionCommand.ABORT, refId));
    let activeRefId = this.getter.getActivelyDisplayedRefId(refId);
    activeRefId = activeRefId != null ? activeRefId : refId;
    this.coordinator.cmdCloseOrderEntryScreen(activeRefId);
  }

  /**********************************************************************/
  /* MODIFY - Unlock and Close */
  // internalRemoveModifyLockAndClosePopout
  public closeAndUnlockModify(refId: string, closeWindow: boolean): void {
    this.removeModifyLocks(refId);
    let activeRefId = this.getter.getActivelyDisplayedRefId(refId);
    activeRefId = activeRefId != null ? activeRefId : refId;
    if (closeWindow) {
      this.coordinator.cmdCloseOrderEntryScreen(activeRefId);
    }
  }

  public revalButtons(refId: string): void {
    this.removeModifyLocks(refId);
    let activeRefId = this.getter.getActivelyDisplayedRefId(refId);
    activeRefId = activeRefId != null ? activeRefId : refId;
    this.coordinator.cmdCloseOrderEntryScreen(activeRefId);
  }

  //actionRemoveModifyLocks
  private removeModifyLocks(refId: string): void {
    if (this.getter.has(refId)) {
      let orderIds = new Array<string>();
      let nodeList = this.getter.getStrategyNodesListRoot(refId);
      nodeList.forEach((itemRefId: string) => {
        if (this.getter.has(itemRefId)) {
          let orderId = this.getter.getOrderId(itemRefId);
          let isLocked = this.getter.isLockedForModification(itemRefId);
          if (orderId != null && isLocked) {
            orderIds.push(orderId);
          }
        }
      });
      logger.out.info(`DEV-TEST: oeCloser: MODIFY UNLOCK orderIds= ${orderIds.toString()}`);

      if (orderIds.length > 0) {
        let action: OENActionCommand = OENActionCommand.UNLOCK_MODIFY;
        let isBmark: boolean = this.getter.isBenchmarkRequest(refId);
        this.oeActionsControlSource.next(new OEActionCommandModel(action, orderIds, isBmark));
        //this.actions.unlockModify(orderIds);
      }
    }
  }
  /**********************************************************************/
  /* AMEND MARKUP - Unlock & Close */

  // internalRemoveAmendMarkupLockAndClosePopout
  public closeAndUnlockAmendMarkup(refId: string): void {
    this.removeAmendMarkupLocks(refId);
    let activeRefId = this.getter.getActivelyDisplayedRefId(refId);
    activeRefId = activeRefId != null ? activeRefId : refId;
    this.coordinator.cmdCloseOrderEntryScreen(activeRefId);
  }

  // actionRemoveAmendMarkupLocks
  private removeAmendMarkupLocks(refId: string): void {
    if (this.getter.has(refId)) {
      let orderIds = new Array<string>();
      let nodeList = this.getter.getStrategyNodesListRoot(refId);
      nodeList.forEach((itemRefId: string) => {
        if (this.getter.has(itemRefId)) {
          let orderId = this.getter.getOrderId(itemRefId);
          if (orderId != null) {
            orderIds.push(orderId);
          }
        }
      });
      logger.out.info(`DEV-TEST: oeCloser: AMEND MARKUP UNLOCK orderIds= ${orderIds.toString()}`);
      if (orderIds.length > 0) {
        //this.actions.unlockAmendMarkup(orderIds);
        this.oeActionsControlSource.next(
          new OEActionCommandModel(OENActionCommand.UNLOCK_AMEND_MARKUP, orderIds),
        );
      }
    }
  }
}
